/**
 * Dunyo soatlari: S -- bitta katta soat, M -- 4 ta soat qatorda (Apple World Clock kabi), L -- soatlar + qo'ng'iroq
 * lentasi (har zonada mahalliy qo'ng'iroq vaqti yashil, hozirgi vaqt chizig'i). Yuqori panelda -- raqamli ixcham.
 */
import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useNow } from "@/lib/useNow";
import { AnalogFace } from "./AnalogFace";
import type { ClockConfig, ClockZone } from "./config";
import { zoneName } from "./names";
import {
  autoHour12,
  clockText,
  dayDelta,
  formatOffset,
  inCallHours,
  isDaytime,
  relativeOffset,
  timeline,
  TIMELINE_BEFORE_H,
  TIMELINE_HOURS,
  zoneParts,
  zoneShort,
  type Band,
} from "./time";

type Props = { item: WidgetItem<ClockConfig>; me: Me };

const homeOf = (me: Me) => me.timezone || "Asia/Tashkent";

function useHour12(config: ClockConfig) {
  const { i18n } = useTranslation();
  return config.hour12 ?? autoHour12(i18n.resolvedLanguage ?? i18n.language);
}

/** "Bugun" / "Ertaga" / "Kecha" va farq ("−10 soat"); uy zonasining o'zida -- faqat kun */
function Relative({ tz, home, now, className }: { tz: string; home: string; now: Date; className?: string }) {
  const { t } = useTranslation("widgets");
  const delta = dayDelta(now, tz, home);
  const offset = relativeOffset(now, tz, home);
  return (
    <div className={cn("text-[11px] leading-tight text-muted", className)}>
      <div className="truncate">{t(`clock.day.${delta < 0 ? "yesterday" : delta > 0 ? "tomorrow" : "today"}`)}</div>
      {offset !== 0 && <div className="truncate">{t("clock.hours", { value: formatOffset(offset) })}</div>}
    </div>
  );
}

function FaceColumn({ zone, size, seconds, home, now }: { zone: ClockZone; size: number; seconds: boolean; home: string; now: Date }) {
  const { t } = useTranslation("widgets");
  const name = zoneName(zone, t);
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center text-center">
      <AnalogFace tz={zone.tz} size={size} seconds={seconds} />
      <div className="mt-1.5 w-full truncate text-[12px] font-semibold" title={name}>
        {name}
      </div>
      <Relative tz={zone.tz} home={home} now={now} className="w-full" />
    </div>
  );
}

export function ClockView({ item, me }: Props) {
  const now = useNow("minute");
  const c = item.config;
  const home = homeOf(me);
  if (item.size === "s") {
    const zone = c.zones[0];
    return (
      <div className="flex h-full flex-col items-center justify-center px-3 py-2.5">
        <FaceColumn zone={zone} size={84} seconds={c.seconds} home={home} now={now} />
      </div>
    );
  }
  const faces = (
    <div className="flex gap-1.5">
      {c.zones.slice(0, 4).map((z, i) => (
        <FaceColumn key={`${z.tz}-${i}`} zone={z} size={item.size === "l" ? 50 : 56} seconds={c.seconds} home={home} now={now} />
      ))}
    </div>
  );
  if (item.size === "m") return <div className="flex h-full flex-col justify-center px-3.5 py-3">{faces}</div>;
  return (
    <div className="flex h-full flex-col justify-center gap-5 px-3.5 py-3">
      {faces}
      <CallTimeline config={c} home={home} now={now} />
    </div>
  );
}

const BAND: Record<Band, string> = {
  call: "bg-clock-band-call",
  day: "bg-clock-band-day",
  night: "bg-clock-band-night",
};
// Qator: nom + vaqt ustunlari, qolgani -- lenta
const NAME_W = 58;
const TIME_W = 50;
const GAP = 6;
const BAR_LEFT = NAME_W + TIME_W + GAP * 2;

/** Qo'ng'iroq lentasi: hozirdan 6 soat oldin -- 18 soat keyin; o'qda -- uy vaqti */
function CallTimeline({ config, home, now }: { config: ClockConfig; home: string; now: Date }) {
  const { t } = useTranslation("widgets");
  const hour12 = useHour12(config);
  const nowAt = TIMELINE_BEFORE_H / TIMELINE_HOURS;
  // O'q uy vaqtida: hozir (chiziq ostida) va har 6 soat
  const ticks = [0, 6, 12, 18].map((h) => {
    if (h === TIMELINE_BEFORE_H) return { at: nowAt, label: t("clock.now") };
    const p = zoneParts(new Date(now.getTime() + (h - TIMELINE_BEFORE_H) * 3_600_000), home);
    const { time, suffix } = clockText({ hour: p.hour, minute: 0 }, hour12);
    return { at: h / TIMELINE_HOURS, label: hour12 ? `${time.replace(":00", "")}${suffix.toLowerCase()}` : time };
  });
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    <div>
      <div className="relative space-y-1" style={{ ["--bar-left" as string]: `${BAR_LEFT}px` }}>
        {config.zones.map((z, i) => {
          const p = zoneParts(now, z.tz);
          const { time, suffix } = clockText(p, hour12);
          const calling = inCallHours(p, config.call);
          const name = zoneName(z, t);
          return (
            <div key={`${z.tz}-${i}`} className="flex items-center text-[11px]" style={{ gap: GAP }}>
              <span className="truncate font-medium" style={{ width: NAME_W }} title={name}>
                {name}
              </span>
              <span className={cn("shrink-0 tabular-nums", calling ? "font-semibold text-ink" : "text-muted")} style={{ width: TIME_W }}>
                {time}
                {suffix && <span className="ml-0.5 text-[9px]">{suffix}</span>}
              </span>
              <div className="relative h-[7px] flex-1 overflow-hidden rounded-full">
                {timeline(now, z.tz, config.call).map((s) => (
                  <span key={s.from} className={cn("absolute inset-y-0", BAND[s.kind])} style={{ left: `${s.from * 100}%`, width: `${(s.to - s.from) * 100}%` }} />
                ))}
              </div>
            </div>
          );
        })}
        {/* Hozirgi vaqt: hamma lentalar ustidan bitta chiziq */}
        <span aria-hidden className="absolute -top-0.5 -bottom-0.5 w-[1.5px] rounded-full bg-clock-accent" style={{ left: `calc(var(--bar-left) + (100% - var(--bar-left)) * ${nowAt})` }} />
      </div>
      <div className="relative mt-1 h-3 text-[9.5px] text-muted" style={{ marginLeft: BAR_LEFT }}>
        {ticks.map((tk) => (
          <span key={tk.at} className={cn("absolute -translate-x-1/2 tabular-nums first:translate-x-0", tk.at === nowAt && "font-semibold text-clock-accent")} style={{ left: `${tk.at * 100}%` }}>
            {tk.label}
          </span>
        ))}
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted">
        <span className="h-1.5 w-3 rounded-full bg-clock-band-call" aria-hidden />
        {t("clock.callLegend", { from: `${pad(config.call[0])}:00`, to: `${pad(config.call[1] % 24)}:00` })}
      </div>
    </div>
  );
}

/** Yuqori panel: 1--3 zona (ekran toraysa kamroq), kun/tun belgisi bilan */
export function ClockGlance({ item }: Props) {
  const { t } = useTranslation("widgets");
  const now = useNow("minute");
  const hour12 = useHour12(item.config);
  return (
    <span className="flex items-center gap-3">
      {item.config.zones.slice(0, 3).map((z, i) => {
        const p = zoneParts(now, z.tz);
        const { time, suffix } = clockText(p, hour12);
        const Icon = isDaytime(p.hour) ? Sun : Moon;
        return (
          <span key={`${z.tz}-${i}`} className={cn("items-center gap-1", i === 0 ? "inline-flex" : i === 1 ? "hidden lg:inline-flex" : "hidden xl:inline-flex")} title={zoneName(z, t)}>
            <Icon size={12} strokeWidth={1.75} className="text-muted" aria-hidden />
            <span className="max-w-[64px] truncate text-[11px] font-semibold text-muted">{z.label || zoneShort(z.tz)}</span>
            <span className="text-[13px] font-semibold tabular-nums">{time}</span>
            {suffix && <span className="text-[10px] font-medium text-muted">{suffix}</span>}
          </span>
        );
      })}
    </span>
  );
}
